// Utility to verify which brands have been migrated
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { sanitizeBrandName } from './imageUpload';

/**
 * Check if an image URL is a Firebase Storage URL or base64
 */
export const isFirebaseStorageUrl = (url) => {
  return url && url.includes('firebasestorage.googleapis.com');
};

export const isBase64Image = (url) => {
  return url && url.startsWith('data:image/');
};

/**
 * Get migration status for a specific brand
 */
export const getBrandMigrationStatus = async (brandId) => {
  try {
    const brandRef = doc(db, 'brands', brandId);
    const brandSnapshot = await getDoc(brandRef);

    if (!brandSnapshot.exists()) {
      return { error: 'Brand not found' };
    }

    const brandData = brandSnapshot.data();

    // Check promotions
    const promotionsRef = collection(brandRef, 'promotions');
    const promotionsSnapshot = await getDocs(promotionsRef);

    let promotionStatus = {
      total: promotionsSnapshot.size,
      migrated: 0,
      base64: 0
    };

    promotionsSnapshot.forEach(doc => {
      const promotion = doc.data();
      if (isFirebaseStorageUrl(promotion.image)) {
        promotionStatus.migrated++;
      } else if (isBase64Image(promotion.image)) {
        promotionStatus.base64++;
      }
    });

    return {
      brandId,
      name: brandData.name,
      logo: {
        isFirebaseStorage: isFirebaseStorageUrl(brandData.logoImage),
        isBase64: isBase64Image(brandData.logoImage),
        url: brandData.logoImage?.substring(0, 100) + '...'
      },
      cover: {
        isFirebaseStorage: isFirebaseStorageUrl(brandData.coverImage),
        isBase64: isBase64Image(brandData.coverImage),
        url: brandData.coverImage?.substring(0, 100) + '...'
      },
      promotions: promotionStatus,
      migratedAt: brandData.migratedAt?.toDate(),
      fullyMigrated: isFirebaseStorageUrl(brandData.logoImage) &&
                    isFirebaseStorageUrl(brandData.coverImage) &&
                    promotionStatus.base64 === 0
    };
  } catch (error) {
    console.error('Error checking brand migration status:', error);
    return { error: error.message };
  }
};

/**
 * Get overview of all brands migration status
 */
export const getAllBrandsMigrationStatus = async () => {
  try {
    const brandsRef = collection(db, 'brands');
    const brandsSnapshot = await getDocs(brandsRef);

    const summary = {
      total: brandsSnapshot.size,
      fullyMigrated: 0,
      partiallyMigrated: 0,
      notMigrated: 0,
      details: []
    };

    for (const brandDoc of brandsSnapshot.docs) {
      const status = await getBrandMigrationStatus(brandDoc.id);

      if (status.fullyMigrated) {
        summary.fullyMigrated++;
      } else if (status.logo.isFirebaseStorage || status.cover.isFirebaseStorage || status.promotions.migrated > 0) {
        summary.partiallyMigrated++;
      } else {
        summary.notMigrated++;
      }

      summary.details.push(status);
    }

    return summary;
  } catch (error) {
    console.error('Error getting migration overview:', error);
    return { error: error.message };
  }
};

/**
 * Find recently migrated brands
 */
export const getRecentlyMigratedBrands = async () => {
  try {
    const brandsRef = collection(db, 'brands');
    const brandsSnapshot = await getDocs(brandsRef);

    const recentlyMigrated = [];

    for (const brandDoc of brandsSnapshot.docs) {
      const brandData = brandDoc.data();
      if (brandData.migratedAt) {
        const status = await getBrandMigrationStatus(brandDoc.id);
        recentlyMigrated.push({
          ...status,
          migratedAt: brandData.migratedAt.toDate()
        });
      }
    }

    // Sort by migration date (newest first)
    recentlyMigrated.sort((a, b) => b.migratedAt - a.migratedAt);

    return recentlyMigrated;
  } catch (error) {
    console.error('Error getting recently migrated brands:', error);
    return [];
  }
};

/**
 * Verify the new brand name folder structure implementation
 */
export const verifyFolderStructure = async () => {
  console.log('🔍 Verifying brand name folder structure...\n');

  try {
    // Get all brands
    const brandsRef = collection(db, 'brands');
    const brandsSnapshot = await getDocs(brandsRef);

    console.log(`📊 Found ${brandsSnapshot.size} brands to verify\n`);

    const verificationResults = [];

    for (const brandDoc of brandsSnapshot.docs) {
      const brandData = brandDoc.data();
      const brandId = brandDoc.id;
      const brandName = brandData.name;

      // Generate expected folder name using the new system
      const expectedFolderName = sanitizeBrandName(brandName, brandId);

      const result = {
        brandId,
        brandName: brandName || 'Unnamed Brand',
        expectedFolderName,
        hasLogo: !!brandData.logoImage,
        hasCover: !!brandData.coverImage,
        logoIsMigrated: brandData.logoImage && brandData.logoImage.includes('firebase'),
        coverIsMigrated: brandData.coverImage && brandData.coverImage.includes('firebase'),
        logoUrl: brandData.logoImage || null,
        coverUrl: brandData.coverImage || null
      };

      verificationResults.push(result);

      // Log brand info
      console.log(`🏢 Brand: ${result.brandName}`);
      console.log(`   📁 Expected folder: brands/${expectedFolderName}/`);
      console.log(`   🖼️  Logo: ${result.hasLogo ? (result.logoIsMigrated ? '✅ Migrated' : '🔄 Base64') : '❌ None'}`);
      console.log(`   📷 Cover: ${result.hasCover ? (result.coverIsMigrated ? '✅ Migrated' : '🔄 Base64') : '❌ None'}`);

      if (result.logoIsMigrated && result.logoUrl) {
        const logoPath = extractStoragePath(result.logoUrl);
        console.log(`   📂 Logo path: ${logoPath}`);
      }

      if (result.coverIsMigrated && result.coverUrl) {
        const coverPath = extractStoragePath(result.coverUrl);
        console.log(`   📂 Cover path: ${coverPath}`);
      }

      console.log(''); // Empty line for readability
    }

    // Summary
    const totalBrands = verificationResults.length;
    const brandsWithMigratedLogos = verificationResults.filter(r => r.logoIsMigrated).length;
    const brandsWithMigratedCovers = verificationResults.filter(r => r.coverIsMigrated).length;
    const brandsWithBase64Logos = verificationResults.filter(r => r.hasLogo && !r.logoIsMigrated).length;
    const brandsWithBase64Covers = verificationResults.filter(r => r.hasCover && !r.coverIsMigrated).length;

    console.log('📋 VERIFICATION SUMMARY:');
    console.log(`   🏢 Total brands: ${totalBrands}`);
    console.log(`   ✅ Migrated logos: ${brandsWithMigratedLogos}`);
    console.log(`   ✅ Migrated covers: ${brandsWithMigratedCovers}`);
    console.log(`   🔄 Base64 logos remaining: ${brandsWithBase64Logos}`);
    console.log(`   🔄 Base64 covers remaining: ${brandsWithBase64Covers}`);

    return {
      totalBrands,
      brandsWithMigratedLogos,
      brandsWithMigratedCovers,
      brandsWithBase64Logos,
      brandsWithBase64Covers,
      verificationResults
    };

  } catch (error) {
    console.error('❌ Verification failed:', error);
    throw error;
  }
};

/**
 * Extract storage path from Firebase Storage URL
 */
const extractStoragePath = (downloadURL) => {
  try {
    if (!downloadURL || !downloadURL.includes('firebase')) {
      return 'Not a Firebase Storage URL';
    }

    // Extract the file path from the URL
    const urlParts = downloadURL.split('/o/')[1];
    if (!urlParts) return 'Invalid URL format';

    const filePath = decodeURIComponent(urlParts.split('?')[0]);
    return filePath;
  } catch (error) {
    return 'Error parsing URL';
  }
};

/**
 * Test the sanitizeBrandName function with various inputs
 */
export const testSanitizeBrandName = () => {
  console.log('🧪 Testing sanitizeBrandName function...\n');

  const testCases = [
    { name: 'ABC Casino', id: 'brand123', expected: 'ABC_Casino_brand123' },
    { name: 'Super-Lucky Gaming!', id: 'xyz789', expected: 'Super-Lucky_Gaming_xyz789' },
    { name: '123 Online Casino & Sports', id: 'test456', expected: '123_Online_Casino_Sports_test456' },
    { name: '', id: 'empty001', expected: 'empty001' },
    { name: null, id: 'null002', expected: 'null002' },
    { name: '   Padded Spaces   ', id: 'pad003', expected: 'Padded_Spaces_pad003' },
    { name: 'Very Long Brand Name That Should Be Truncated Because It Exceeds Fifty Characters', id: 'long004', expected: 'Very_Long_Brand_Name_That_Should_Be_Truncated_B_long004' }
  ];

  testCases.forEach(testCase => {
    const result = sanitizeBrandName(testCase.name, testCase.id);
    const passed = result.startsWith(testCase.expected.substring(0, 20)); // Check first 20 chars

    console.log(`Input: "${testCase.name}" (${testCase.id})`);
    console.log(`Result: "${result}"`);
    console.log(`Status: ${passed ? '✅ Pass' : '❌ Fail'}`);
    console.log('');
  });
};

/**
 * Check folder structure consistency
 */
export const checkFolderConsistency = async () => {
  console.log('🔄 Checking folder structure consistency...\n');

  try {
    const results = await verifyFolderStructure();

    // Check for potential issues
    const issues = [];

    results.verificationResults.forEach(brand => {
      // Check if migrated images follow the expected folder structure
      if (brand.logoIsMigrated && brand.logoUrl) {
        const logoPath = extractStoragePath(brand.logoUrl);
        const expectedPrefix = `brands/${brand.expectedFolderName}/logo`;

        if (!logoPath.startsWith(expectedPrefix)) {
          issues.push({
            type: 'logo_path_mismatch',
            brand: brand.brandName,
            expectedPath: expectedPrefix,
            actualPath: logoPath
          });
        }
      }

      if (brand.coverIsMigrated && brand.coverUrl) {
        const coverPath = extractStoragePath(brand.coverUrl);
        const expectedPrefix = `brands/${brand.expectedFolderName}/cover`;

        if (!coverPath.startsWith(expectedPrefix)) {
          issues.push({
            type: 'cover_path_mismatch',
            brand: brand.brandName,
            expectedPath: expectedPrefix,
            actualPath: coverPath
          });
        }
      }
    });

    if (issues.length === 0) {
      console.log('✅ All migrated images follow the expected folder structure!');
    } else {
      console.log(`⚠️  Found ${issues.length} folder structure issues:`);
      issues.forEach(issue => {
        console.log(`   - ${issue.brand}: ${issue.type}`);
        console.log(`     Expected: ${issue.expectedPath}`);
        console.log(`     Actual: ${issue.actualPath}`);
      });
    }

    return { issues, totalChecked: results.totalBrands };

  } catch (error) {
    console.error('❌ Consistency check failed:', error);
    throw error;
  }
};